import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/** localStorage key holding the access token (shared by every tab of the browser profile). */
export const AUTH_TOKEN_STORAGE_KEY = 'auth_token';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private static readonly SSO_TOKEN_COOKIE = 'montage_auth_token';
  private static readonly SSO_FIRSTNAME_COOKIE = 'montage_firstname';
  private static readonly SSO_LASTNAME_COOKIE = 'montage_lastname';
  private static readonly SSO_EMAIL_COOKIE = 'montage_email';

  private readonly http = inject(HttpClient);
  private readonly ssoUrl = environment.ssoUrl;
  private redirectUrl: string | null = null;
  private postLoginRedirectUrl: string | null = null;
  private unauthorizedHandled = false;

  constructor(private router: Router) { }

  login(): void {
    window.location.href = this.ssoUrl;
  }

  /**
   * True when SSO just redirected back with a handoff token cookie.
   */
  hasSsoCallback(): boolean {
    return !!this.getCookie(AuthService.SSO_TOKEN_COOKIE);
  }

  /**
   * Persists SSO callback token and profile fields from handoff cookies. Does not navigate —
   * callers must load RBAC and route only after permissions are ready.
   */
  handleAuthCallback(): boolean {
    try {
      const token = this.getCookie(AuthService.SSO_TOKEN_COOKIE);
      if (!token) {
        if (window.location.pathname.includes('device-list')) {
          console.warn(
            'SSO handoff cookie montage_auth_token not found on device-list. ' +
              'Check user-service Set-Cookie Domain and coordinated deploy.'
          );
        }
        return false;
      }

      if (this.getToken() === token) {
        this.clearSsoHandoffCookies();
        return true;
      }

      const pendingRedirect = this.getRedirectUrl();
      localStorage.clear();
      sessionStorage.clear();

      localStorage.setItem('auth_token', token);
      localStorage.setItem('auth_provider', 'sso');

      const firstname = this.getCookie(AuthService.SSO_FIRSTNAME_COOKIE);
      const lastname = this.getCookie(AuthService.SSO_LASTNAME_COOKIE);
      const email = this.getCookie(AuthService.SSO_EMAIL_COOKIE);

      if (firstname) localStorage.setItem('firstname', firstname);
      if (lastname) localStorage.setItem('lastname', lastname);
      if (email) localStorage.setItem('user_email', email);

      this.clearSsoHandoffCookies();
      this.hydrateUserProfileFromToken();
      this.postLoginRedirectUrl = pendingRedirect;
      return true;
    } catch (error) {
      console.error('Error in handleAuthCallback:', error);
      return false;
    }
  }

  /**
   * Exchanges slim SSO handoff JWT for a full session JWT (RBAC scope claims).
   * Skipped when localStorage already holds a session token.
   */
  exchangeSsoToken(): Observable<boolean> {
    const token = this.getToken();
    if (!token) {
      return of(false);
    }

    if (this.isSessionToken(token)) {
      return of(true);
    }

    return this.http
      .post<{ id_token?: string }>(
        `${environment.userServiceUrl}/auth/token-exchange`,
        {}
      )
      .pipe(
        map((response) => {
          const fullToken = response?.id_token?.trim();
          if (!fullToken) {
            console.error('Token exchange response missing id_token');
            return false;
          }
          localStorage.setItem('auth_token', fullToken);
          this.hydrateUserProfileFromToken();
          return true;
        }),
        catchError((error) => {
          console.error('SSO token exchange failed', error);
          return of(false);
        })
      );
  }

  consumePostLoginRedirectUrl(): string | null {
    const url = this.postLoginRedirectUrl;
    this.postLoginRedirectUrl = null;
    return url;
  }

  getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  /**
   * Identifies which user a JWT belongs to ({@code sub}, then {@code userId}).
   * SSO handoff and session tokens of the same user share the same subject.
   */
  getTokenSubject(token: string | null | undefined): string | null {
    if (!token) {
      return null;
    }
    const payload = this.decodeJwtPayload(token);
    if (!payload) {
      return null;
    }
    const userId = payload['userId'];
    return (
      this.readString(payload['sub']) ??
      (userId != null && String(userId).trim() !== '' ? String(userId).trim() : null)
    );
  }

  /** Resolves a display label for the header from storage and JWT claims. */
  getDisplayName(): string {
    this.hydrateUserProfileFromToken();

    const firstName = localStorage.getItem('firstname')?.trim() ?? '';
    const lastName = localStorage.getItem('lastname')?.trim() ?? '';
    const fullName = `${firstName} ${lastName}`.trim();
    if (fullName) {
      return fullName;
    }

    const displayName = localStorage.getItem('display_name')?.trim();
    if (displayName) {
      return displayName;
    }

    const email = localStorage.getItem('user_email')?.trim();
    if (email) {
      return email;
    }

    return '';
  }

  /**
   * Fills missing profile fields from JWT payload (SSO / login2 often omit firstname in storage).
   */
  hydrateUserProfileFromToken(): void {
    const profile = this.parseTokenProfile();
    if (!profile) {
      return;
    }

    if (!localStorage.getItem('firstname')?.trim() && profile.firstname) {
      localStorage.setItem('firstname', profile.firstname);
    }
    if (!localStorage.getItem('lastname')?.trim() && profile.lastname) {
      localStorage.setItem('lastname', profile.lastname);
    }
    if (!localStorage.getItem('display_name')?.trim() && profile.displayName) {
      localStorage.setItem('display_name', profile.displayName);
    }
    if (!localStorage.getItem('user_email')?.trim() && profile.email) {
      localStorage.setItem('user_email', profile.email);
    }
    if (!localStorage.getItem('user_id')?.trim() && profile.userId) {
      localStorage.setItem('user_id', profile.userId);
    }
  }

  /**
   * True only when a non-expired access token is stored.
   * An expired token is removed so later requests are not sent with it.
   * {@code auth_provider} is left in place so the user returns to the matching login page.
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }
    if (this.isAccessTokenExpired(token)) {
      this.discardExpiredAccessToken();
      return false;
    }
    return true;
  }

  /**
   * True when the JWT payload cannot be read or {@code exp} is in the past.
   * {@code exp} is the standard NumericDate (seconds).
   */
  isAccessTokenExpired(token: string): boolean {
    const payload = this.decodeJwtPayload(token);
    if (!payload) {
      return true;
    }
    const expSeconds = this.readExpiryEpochSeconds(payload['exp']);
    if (expSeconds == null) {
      return true;
    }
    return Date.now() >= expSeconds * 1000;
  }

  /**
   * Ends the SPA session after the server rejects the access token (401/440).
   * Safe to call from several in-flight requests; only the first redirect runs.
   */
  handleUnauthorizedResponse(): void {
    if (this.unauthorizedHandled) {
      return;
    }
    this.unauthorizedHandled = true;
    this.logout();
  }

  isLogin2Session(): boolean {
    return localStorage.getItem('auth_provider')?.trim().toLowerCase() === 'login2';
  }

  getLoginRoute(): '/login' | '/login2' {
    return this.isLogin2Session() ? '/login2' : '/login';
  }

  logout(): void {
    const loginRoute = this.getLoginRoute();
    const isCredentialSession = this.isLogin2Session();
    const tenantId = this.resolveTenantIdFromToken();
    const loginReturnUrl = `${window.location.origin}${loginRoute}`;

    localStorage.clear();
    sessionStorage.clear();
    this.clearSsoHandoffCookies();

    if (isCredentialSession) {
      window.location.replace(loginReturnUrl);
      return;
    }

    window.location.replace(this.buildSsoLogoutUrl(loginReturnUrl, tenantId));
  }

  /** SSO logout via user-service → Entra ID sign-out → return to app login. */
  private buildSsoLogoutUrl(
    postLogoutRedirectUri: string,
    tenantId: string | null
  ): string {
    const encodedRedirect = encodeURIComponent(postLogoutRedirectUri);
    const serviceBase = environment.userServiceUrl.trim().replace(/\/api\/?$/, '');
    return `${serviceBase}/azure/logout?post_logout_redirect_uri=${encodedRedirect}${
      tenantId ? `&tenant=${encodeURIComponent(tenantId)}` : ''
    }`;
  }

  private resolveTenantIdFromToken(): string | null {
    const payload = this.decodeJwtPayload(this.getToken() ?? '');
    if (!payload) {
      return null;
    }
    return this.readString(payload['tid']) ?? null;
  }

  setRedirectUrl(url: string): void {
    const safeUrl = this.sanitizeRedirectUrl(url);
    this.redirectUrl = safeUrl;
    if (safeUrl) {
      localStorage.setItem('redirectUrl', safeUrl);
    } else {
      localStorage.removeItem('redirectUrl');
    }
  }

  getRedirectUrl(): string | null {
    return this.sanitizeRedirectUrl(localStorage.getItem('redirectUrl'));
  }

  consumeRedirectUrl(): string | null {
    const safeUrl = this.getRedirectUrl();
    this.clearRedirectUrl();
    return safeUrl;
  }

  clearRedirectUrl(): void {
    localStorage.removeItem('redirectUrl');
    this.redirectUrl = null;
  }

  /** Only allow same-origin relative routes — never redirect to deployed dev/uat/prod hosts. */
  private sanitizeRedirectUrl(url: string | null | undefined): string | null {
    if (!url?.trim()) {
      return null;
    }

    const value = url.trim();
    if (value.startsWith('/') && !value.startsWith('//')) {
      return value;
    }

    try {
      const parsed = new URL(value);
      if (parsed.origin === window.location.origin) {
        return `${parsed.pathname}${parsed.search}${parsed.hash}`;
      }
    } catch {
      return null;
    }

    return null;
  }

  private parseTokenProfile(): {
    firstname?: string;
    lastname?: string;
    displayName?: string;
    email?: string;
    userId?: string;
  } | null {
    const token = this.getToken();
    if (!token) {
      return null;
    }

    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }

    try {
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
      const payload = JSON.parse(atob(padded)) as Record<string, unknown>;

      const firstname = this.readString(
        payload['given_name'] ??
          payload['firstName'] ??
          payload['firstname'] ??
          payload['first_name']
      );
      const lastname = this.readString(
        payload['family_name'] ??
          payload['lastName'] ??
          payload['lastname'] ??
          payload['last_name']
      );
      const displayName = this.readString(
        payload['name'] ?? payload['displayName'] ?? payload['display_name']
      );
      const email = this.readString(
        payload['email'] ??
          payload['preferred_username'] ??
          payload['unique_name'] ??
          payload['upn'] ??
          payload['username'] ??
          payload['userName']
      );
      const userId = this.readString(
        payload['userId'] ?? payload['user_id'] ?? payload['sub']
      );

      if (!firstname && !lastname && !displayName && !email) {
        return null;
      }

      return { firstname, lastname, displayName, email, userId };
    } catch {
      return null;
    }
  }

  private discardExpiredAccessToken(): void {
    localStorage.removeItem('auth_token');
  }

  private readExpiryEpochSeconds(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }

  private readString(value: unknown): string | undefined {
    if (typeof value !== 'string') {
      return undefined;
    }
    const trimmed = value.trim();
    return trimmed || undefined;
  }

  private isSessionToken(token: string): boolean {
    const payload = this.decodeJwtPayload(token);
    if (!payload) {
      return false;
    }
    if (payload['tokenType'] === 'SESSION') {
      return true;
    }
    return (
      payload['superAdmin'] === true ||
      Array.isArray(payload['adminCust']) ||
      Array.isArray(payload['viewCust']) ||
      Array.isArray(payload['memberCust'])
    );
  }

  private decodeJwtPayload(token: string): Record<string, unknown> | null {
    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }

    try {
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
      return JSON.parse(atob(padded)) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  private getCookie(name: string): string | null {
    const prefix = `${name}=`;
    const entry = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(prefix));
    if (!entry) {
      return null;
    }

    const raw = entry.substring(prefix.length);
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }

  private clearSsoHandoffCookies(): void {
    const cookieNames = [
      AuthService.SSO_TOKEN_COOKIE,
      AuthService.SSO_FIRSTNAME_COOKIE,
      AuthService.SSO_LASTNAME_COOKIE,
      AuthService.SSO_EMAIL_COOKIE,
    ];

    const domain = environment.ssoCookieDomain?.trim();
    const domainPart = domain ? `; Domain=${domain}` : '';
    const securePart = domain ? '; Secure' : '';

    for (const name of cookieNames) {
      document.cookie = `${name}=; Path=/${domainPart}; Max-Age=0; SameSite=Lax${securePart}`;
    }
  }
}
